/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useMemo} from 'react';
import Grid from '@material-ui/core/Grid';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {Device} from '@ion/core/src/types/entities';
import {isMDTDevice as isValidMDTDevice} from '@ion/core/src/utils/DeviceUtils';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {getSelectedDevice} from '@ion/dls/src/editors/helpers';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {getLastIdReport, getLastReportedTime} from './helpers';
import {
  getConfigVersion,
  getFirmwareVersion
} from './ViewDeviceEditorUtils';

export interface DeviceInfoPanelType {
  device: Device;
  deviceDetail: Device;
  latestIdReport?: any;
  explicitReportDate?: boolean;
  isCtcAdmin?: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  gridContainer : {
    borderRadius    : '4px',
    width           : '100%',
    backgroundColor : dlsColor('white'),
    paddingTop      : '10px'
  },
  headerItem : {
    ...dlsAlt('typography', 'label2'),
    color    : dlsColor('grey1'),
    fontSize : '14px',
    padding  : '5px 20px'
  },
  bodyItem : {
    ...dlsAlt('typography', 'label3'),
    color        : dlsColor('grey1'),
    fontWeight   : 700,
    fontSize     : '12px',
    padding      : '5px 20px 15px',
    textOverflow : 'ellipsis',
    overflow     : 'hidden'
  },
  nameItem : {
    [theme.breakpoints.down('sm')] : {
      ...dlsAlt('typography', 'cta2')
    }
  },
  caseClass : {
    textTransform : 'lowercase'
  }
}));

/**
 * Info Panel component that displays the Device data for the selected item.
 */
export const DeviceInfoPanel = ({
  device,
  deviceDetail,
  latestIdReport,
  isCtcAdmin = false,
  explicitReportDate = false
}: DeviceInfoPanelType) => {
  const classes        = useStyles();
  const isMDTDevice    = isValidMDTDevice(device);
  const selectedDevice = getSelectedDevice(device, deviceDetail);

  const accountName = useMemo(() => {
    if (selectedDevice && !isObjectEmpty(selectedDevice)) {
      const {account} = selectedDevice || {};
      return `${account?.title || ''} ${account?.href ? `(${entityId(account.href)})` : ''}`;
    }
  }, [deviceDetail, device]);

  return (
    <Grid container
      spacing={0}
      alignItems='flex-start'
      justifyContent='flex-start'
      className={classes.gridContainer}>
      <Grid item md={2}>
        <div className={classes.headerItem}>
          {translate('admin-devices:view.deviceDetailSummary.deviceModel')}
        </div>
        <div className={classes.bodyItem}>
          {deviceDetail?.type?.title || device?.type?.title}
        </div>
      </Grid>
      <Grid item md={2}>
        <div className={classes.headerItem}>
          {translate('admin-devices:view.generalDeviceInfo.account')}
        </div>
        <div className={classes.bodyItem} title={accountName}>
          {accountName}
        </div>
      </Grid>
      {isMDTDevice ? (
        <>
          <Grid item md={2}>
            <div className={classes.headerItem}>
              {translate('admin-devices:view.deviceDetailSummary.androidOSVersion')}
            </div>
            <div className={classes.bodyItem}>
              {device?.extensions?.androidOSVersion}
            </div>
          </Grid>
          <Grid item md={2}>
            <div className={classes.headerItem}>
              {translate('admin-devices:view.deviceDetailSummary.kernelVersion')}
            </div>
            <div className={classes.bodyItem}>
              {device?.extensions?.kernelVersion}
            </div>
          </Grid>
        </>
      ) : (
        <>
          <Grid item md={2}>
            <div className={classes.headerItem}>
              {translate('admin-devices:view.deviceDetailSummary.firmware')}
            </div>
            <div className={classnames(classes.bodyItem, classes.caseClass)}>
              {getFirmwareVersion(device, true)}
            </div>
          </Grid>
          <Grid item md={1}>
            <div className={classes.headerItem}>
              {translate('admin-devices:view.deviceDetailSummary.config')}
            </div>
            <div className={classes.bodyItem}>
              {getConfigVersion(device, true, isCtcAdmin)}
            </div>
          </Grid>
          <Grid item md={1}>
            <div className={classes.headerItem}>
              {translate('admin-devices:view.deviceDetailSummary.appId')}
            </div>
            <div className={classes.bodyItem}>
              {device?.extensions?.appId || ''}
            </div>
          </Grid>
        </>
      )}
      <Grid item md={2}>
        <div className={classes.headerItem}>
          {translate('admin-devices:view.deviceDetailSummary.lastIdReport')}
        </div>
        <div className={classes.bodyItem}>
          {getLastIdReport(device, true, latestIdReport)}
        </div>
      </Grid>
      <Grid item md={2}>
        <div className={classes.headerItem}>
          {translate('admin-devices:view.deviceDetailSummary.lastEventTime')}
        </div>
        <div className={classes.bodyItem}>
          {getLastReportedTime(device)}
        </div>
      </Grid>
    </Grid>
  );
};

export default DeviceInfoPanel;
